import React from "react";
import {
  chunkPairs,
  dimLabelColor,
  formatSpecLineText,
  getDisplaySpecEntries,
  normalizeOrderLineProduct,
} from "./quotationSpecHelpers";

function isInchUom(uom: unknown): boolean {
  const u = String(uom || "")
    .toLowerCase()
    .trim();
  return u === "inch" || u === "in" || u === "inches";
}

function getNoolField(p: Record<string, unknown>, kind: "width" | "height") {
  const specs = (p.specifications || {}) as Record<string, unknown>;
  const fromSpecs = (keys: string[]) => {
    for (const k of keys) {
      const v = specs[k];
      if (v === undefined || v === null || v === "") continue;
      const raw =
        typeof v === "object" && v !== null && "name" in v
          ? (v as { name?: unknown }).name
          : v;
      if (raw !== undefined && raw !== null && raw !== "") return raw;
    }
    return undefined;
  };
  if (kind === "width") {
    return (
      p.nool_width ??
      p.noolWidth ??
      fromSpecs(["nool_width", "noolWidth", "Nool Width"]) ??
      0
    );
  }
  return (
    p.nool_height ??
    p.noolHeight ??
    fromSpecs(["nool_height", "noolHeight", "Nool Height"]) ??
    0
  );
}

type Props = {
  product: Record<string, unknown>;
  className?: string;
};

/** Spec + dimension block matching quotation PDF layout (2 specs per row, key-first, light colors). */
export function ProductLineSpecDisplay({ product, className = "" }: Props) {
  const p = normalizeOrderLineProduct(product);
  const formulaType = String(p.formulaType || "width_height");
  const isWidthHeight = !formulaType || formulaType === "width_height";
  const isLength = formulaType === "length";
  const entries = getDisplaySpecEntries(p);

  const renderDimPart = (label: "W" | "H" | "L", value: unknown, nool: unknown, uom: unknown) => {
    const color = dimLabelColor(label);
    const noolNum = parseFloat(String(nool));
    const showNool = isInchUom(uom) && !Number.isNaN(noolNum) && noolNum > 0;
    const uomDisplay = String(uom || "").trim();
    return (
      <span className="font-bold text-[11px] md:text-xs whitespace-nowrap" style={{ color }}>
        {label}: {String(value ?? "")}
        {showNool ? <sup>{String(noolNum)}</sup> : null}
        {uomDisplay ? ` ${uomDisplay}` : null}
      </span>
    );
  };

  return (
    <div className={`mt-1 space-y-0.5 ${className}`}>
      {entries.length > 0 &&
        chunkPairs(entries).map((row, rowIdx) => (
          <div key={`spec-row-${rowIdx}`} className="grid grid-cols-2 gap-x-2 gap-y-0.5">
            {row.map((entry, cellIdx) => (
              <span
                key={`spec-${rowIdx}-${cellIdx}`}
                className="text-[11px] md:text-xs font-bold break-words min-w-0"
                style={{ color: entry.color }}
              >
                {formatSpecLineText(entry)}
              </span>
            ))}
            {row.length === 1 ? <span className="min-w-0" aria-hidden /> : null}
          </div>
        ))}

      {isWidthHeight && (
        <div className="flex flex-wrap items-center gap-x-1">
          {renderDimPart("W", p.width, getNoolField(p, "width"), p.wh_uom)}
          <span className="text-gray-500 text-xs font-bold">×</span>
          {renderDimPart("H", p.height, getNoolField(p, "height"), p.wh_uom)}
        </div>
      )}

      {isLength && renderDimPart("L", p.length, null, p.length_uom)}
    </div>
  );
}

export function qtyDisplayStyle(): React.CSSProperties {
  return { color: dimLabelColor("Qty"), fontWeight: 700 };
}
